import React from "react";
import { ShieldCheck, Palette, Award, HardHat, Beaker, Search, Factory, Settings, Wrench, Truck, Store } from "lucide-react";
import { SiteSettings, Product } from "../types";

interface HeroProps {
  settings: SiteSettings;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setCurrentTab: (tab: string) => void;
  products: Product[];
  appLanguage: "fa" | "en";
}

export default function Hero({ settings, searchQuery, setSearchQuery, setCurrentTab, products, appLanguage }: HeroProps) {
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);
  const isAlmas = import.meta.env.VITE_TENANT_ID === "almas";

  const searchResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.specs?.nameEn && p.specs.nameEn.toLowerCase().includes(q)) ||
        p.sku.toLowerCase().includes(q) ||
        p.category.some(c => 
          c.toLowerCase().includes(q) || 
          (settings.categoriesEn?.[c] && settings.categoriesEn[c].toLowerCase().includes(q))
        ) ||
        (p.specs?.brand && (
          p.specs.brand.toLowerCase().includes(q) ||
          (settings.brandsEn?.[p.specs.brand] && settings.brandsEn[p.specs.brand].toLowerCase().includes(q))
        ))
    ).slice(0, 5); // حداکثر ۵ نتیجه برای پاپ‌آپ
  }, [searchQuery, products]);

  return (
    <section className="relative z-40 bg-slate-50 text-slate-800 py-10 md:py-14 px-4 border-b border-slate-200" style={{ direction: appLanguage === "fa" ? "rtl" : "ltr" }}>
      
      {/* Background Wrapper to contain overflows without clipping popups */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Dynamic Powder Color Blurs (Simulating Pigment Dust) */}
        {isAlmas ? (
          <>
            <div className="absolute top-[-10%] right-[-5%] w-[40vw] h-[40vw] bg-blue-600/20 rounded-full blur-[120px] mix-blend-multiply animate-pulse" style={{ animationDuration: '8s' }}></div>
            <div className="absolute bottom-[-10%] left-[-5%] w-[35vw] h-[35vw] bg-orange-500/25 rounded-full blur-[120px] mix-blend-multiply animate-pulse" style={{ animationDuration: '12s' }}></div>
            <div className="absolute top-[20%] left-[30%] w-[30vw] h-[30vw] bg-cyan-500/20 rounded-full blur-[120px] mix-blend-multiply"></div>
          </>
        ) : (
          <>
            <div className="absolute top-[-10%] right-[-5%] w-[40vw] h-[40vw] bg-rose-500/20 rounded-full blur-[120px] mix-blend-multiply animate-pulse" style={{ animationDuration: '8s' }}></div>
            <div className="absolute bottom-[-10%] left-[-5%] w-[35vw] h-[35vw] bg-amber-400/25 rounded-full blur-[120px] mix-blend-multiply animate-pulse" style={{ animationDuration: '12s' }}></div>
            <div className="absolute top-[20%] left-[30%] w-[30vw] h-[30vw] bg-sky-500/20 rounded-full blur-[120px] mix-blend-multiply"></div>
          </>
        )}
        
        {/* Grid Pattern Details */}
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,#fff,rgba(255,255,255,0.9))] opacity-60"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-20 text-center space-y-5 md:space-y-6">
        {/* Main Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-sm text-indigo-800 text-xs font-bold">
          <ShieldCheck className="w-4 h-4 text-indigo-500 shrink-0" />
          <span>
            {appLanguage === "en"
              ? (settings.homeHeroBadgeEn || (isAlmas ? "Official Dealer of Industrial & Mining Machinery" : "Certified Technical Grade & Color Accuracy"))
              : (settings.homeHeroBadge || (isAlmas ? "نمایندگی رسمی ماشین‌آلات صنعتی، سنگبری و تجهیزات معادن" : "تضمین اصالت کدهای فنی، دانه‌بندی دقیق و فام شید یکنواخت ثبتی"))}
          </span>
        </div>

        {/* Dynamic Headline */}
        <h2 
          className="font-black text-slate-900 tracking-tight leading-snug md:leading-tight max-w-4xl mx-auto"
          style={{ fontSize: `${settings.homeHeroTitleSize || 36}px` }}
        >
          {appLanguage === "en" 
            ? (settings.homeHeroTitleEn || (isAlmas ? "Advanced Supplier" : "Specialized Center"))
            : (settings.homeHeroTitle || (isAlmas ? "تأمین‌کننده پیشرفته" : "مرکز تخصصی تأمین"))}
          {" "}
          <span className={`text-transparent bg-clip-text bg-gradient-to-l ${isAlmas ? 'from-blue-600 via-cyan-600 to-indigo-600' : 'from-rose-600 via-purple-600 to-indigo-600'}`}>
            {appLanguage === "en"
              ? (settings.homeHeroTitleGradientEn || (isAlmas ? "Stone & Mining Tech" : "Pigments & Powder Coatings"))
              : (settings.homeHeroTitleGradient || (isAlmas ? "ماشین‌آلات سنگبری و معدن" : "رنگ‌های صنعتی و معدنی"))}
          </span>
        </h2>
        
        {/* Descriptive intro paragraph */}
        <p 
          className="text-slate-600 max-w-3xl mx-auto leading-relaxed font-medium"
          style={{ fontSize: `${settings.homeHeroSubtitleSize || 16}px` }}
        >
          {appLanguage === "en"
            ? (settings.homeHeroSubtitleEn || (isAlmas 
                ? "Almas Sarvari Commercial Company, pioneer in importing and supplying industrial machinery."
                : "Peyman Adel Commercial; specialized supplier of iron oxide micronized powders and abrasive tools."))
            : (settings.homeHeroSubtitle || (isAlmas 
                ? "شرکت تجارتی الماس سروری با بیش از دو دهه تجربه، پیشگام در واردات و عرضه انواع دستگاه‌های گنسا، قله‌بر، فرزهای سنگبری، ماشین‌آلات پالش اسلب، پرس موزاییک و دیسک‌های گرانیت بُر در سطح منطقه."
                : "مرکز مشاوره و فروش تخصصی بازرگانی پیمان عادل؛ تدارک متمایز پودرهای میکرونیزه اکسید آهن (اخرا، گل‌ماش، دوده، شیدهای آلی)، ملزومات و ابزارآلات برشی و لقمه‌های پولیش اسیدی کارخانجات سنگبری، سنگ کفسابی و قطعات بتن پیش‌ساخته."))}
        </p>

        {/* Dynamic Multi-Action search block */}
        <div className="max-w-4xl mx-auto mt-6 relative">
          <div className="bg-white/60 backdrop-blur-xl border border-white p-3 rounded-3xl shadow-xl shadow-indigo-900/5 flex flex-col sm:flex-row items-center gap-3">
            <input
              type="text"
              placeholder={isAlmas ? "جستجوی ماشین‌آلات سنگبری، دستگاه پالش، قله‌بر یا سنگ‌شکن..." : "جستجوی فام رنگ، نام پیگمنت شید، برند یا نوع لقمه ساینده..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsSearchFocused(false);
                  const el = document.getElementById("catalog-tools");
                setCurrentTab("store");
                setTimeout(() => { const el = document.getElementById("catalog-root"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 100);
                }
              }}
              className="w-full text-sm font-medium px-5 py-3.5 bg-white/90 border border-slate-200/50 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none rounded-2xl text-slate-800 text-right transition-all placeholder:text-slate-400"
            />
            <button
              onClick={() => {
                // Focus search bar or scroll down
                const el = document.getElementById("catalog-tools");
            setCurrentTab("store");
            setTimeout(() => { const el = document.getElementById("catalog-root"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 100);
              }}
              className={`w-full sm:w-auto px-8 py-3.5 text-white text-sm font-black rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer ${isAlmas ? "bg-gradient-to-l from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 shadow-blue-500/25" : "bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-indigo-500/25"}`}
            >
              <Search className="w-4 h-4" />
              <span>{appLanguage === "en" ? "Search" : "جستجو در کاتالوگ"}</span>
            </button>

            <button
              onClick={() => { setCurrentTab("store"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="w-full sm:w-auto px-6 py-3.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-sm font-black rounded-2xl shadow-sm transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer"
            >
              <Store className="w-4 h-4 text-slate-500" />
              <span>{appLanguage === "en" ? "View Products Online" : "مشاهده محصولات به صورت آنلاین"}</span>
            </button>
          </div>

          {/* Live Search Popup */}
          {isSearchFocused && searchQuery.trim() !== "" && (
            <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 text-right animate-fadeIn">
              {searchResults.length > 0 ? (
                <ul className="divide-y divide-slate-50">
                  {searchResults.map((product) => {
                    const displayName = appLanguage === "en" && product.specs?.nameEn ? product.specs.nameEn : product.name;
                    return (
                    <li
                      key={product.id}
                      className="p-3 hover:bg-indigo-50 cursor-pointer flex items-center justify-between transition-colors group"
                      onMouseDown={(e) => {
                        e.preventDefault(); // جلوگیری از بسته شدن منو پیش از اجرای کلیک
                        setSearchQuery(displayName);
                        setIsSearchFocused(false);
                        setCurrentTab("store");
                        setTimeout(() => { const el = document.getElementById("catalog-root"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 100);
                        
                        // صدور فرمان باز شدن مدالِ این محصول پس از یک مکث کوتاه برای رندر شدن کاتالوگ
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('openProductModal', { detail: product.sku }));
                        }, 100);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <img src={product.image} alt={product.name} className="w-10 h-10 rounded-xl object-cover border border-slate-200" />
                        <div>
                          <p className="text-xs font-bold text-slate-800 group-hover:text-indigo-700 transition-colors line-clamp-1">{displayName}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[150px]">{product.category.join("، ")}</p>
                        </div>
                      </div>
                      <span className="hidden sm:inline-block text-[10px] font-mono text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg border border-indigo-100">{product.sku}</span>
                    </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="p-5 text-center text-xs text-slate-500">
                  هیچ محصولی مشابه "<span className="font-bold text-slate-700">{searchQuery}</span>" یافت نشد.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Value Propositions Grid cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto pt-6 md:pt-8">
          
          {isAlmas ? (
            <>
              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-blue-100/50">
                  <Factory className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">ماشین‌آلات سنگبری</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    انواع دستگاه‌های گنسا، قله‌بر و طولی‌بر پیشرفته.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-amber-300 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-orange-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-orange-100/50">
                  <Settings className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">سگمنت و دیسک برش</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    سگمنت‌های الماسه از سایز ۴۰۰ تا ۲۰۰۰ میلی‌متر.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-teal-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-teal-100/50">
                  <Wrench className="w-5 h-5 text-teal-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">دستگاه‌های کفسابی</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    خطوط پالش اسلب، موزاییک ساب و صاب دستی.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-600 to-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-slate-200/50">
                  <Truck className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">تجهیزات معادن</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    تأمین انواع سنگ‌شکن‌های فکی، چکشی و ماشین‌آلات متفرقه.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-rose-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-rose-100/50">
                  <Palette className="w-5 h-5 text-rose-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">پایداری نوری و فام ثابت</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    تضمین دوام درازمدت در ملات‌های سیمانی نما و عدم کدرشدگی نوری.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-amber-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-amber-100/50">
                  <Beaker className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">انطباق آنالیز و دانه‌بندی</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    ارائه پیگمنت‌های میکرونیزه با مش فوق‌ریز جهت یکنواختی پوشش.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-sky-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-sky-50 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-sky-100/50">
                  <Award className="w-5 h-5 text-sky-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">اصالت برندهای جهانی</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    واردات مستقیم و تضمینی لانکسس، کرونوس و برندهای برتر بازار.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-lg shadow-slate-200/30 hover:-translate-y-1 transition-all duration-300 flex items-center text-right gap-3 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-600 to-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm border border-slate-200/50">
                  <HardHat className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">لقمه‌های ساینده سنگی</h4>
                  <p className="text-[9px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    لقمه‌های کله‌پفی و اسیدی با طول عمر عالی جهت درخشش گرانیت.
                  </p>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </section>
  );
}
